import React, { useEffect, useState, useCallback } from "react";
import { api, API } from "../lib/api";
import { inr, inrRound } from "../lib/format";
import { PageHeader, Card, Btn, Modal, Field, Input, Select, Badge, EmptyState } from "../components/kr";
import { DatePicker } from "../components/DatePicker";
import Letterhead from "../components/Letterhead";
import { useConfirm } from "../components/ConfirmDialog";
import { useAuth } from "../lib/auth";
import { useMultiSelect } from "../lib/multiSelect";
import MultiSelectBar from "../components/MultiSelectBar";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, Calculator, Printer, WhatsappLogo, Paperclip, DownloadSimple, FileText, UploadSimple, UserMinus, ArrowClockwise } from "@phosphor-icons/react";
import { printDom } from "../lib/printDom";

export default function Employees() {
  const { confirm } = useConfirm();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const ms = useMultiSelect();
  const [emps, setEmps] = useState([]);
  const [open, setOpen] = useState(null);
  const [pay, setPay] = useState(null);

  const load = async () => setEmps((await api.get("/employees")).data);
  useEffect(() => { load(); }, []);

  const del = async (e) => {
    const ok = await confirm({
      title: "Delete employee?",
      message: `${e.name} (${e.emp_id}) will be removed from the payroll and attendance sheets.`,
      tone: "danger",
      confirmLabel: "Delete Employee",
    });
    if (!ok) return;
    await api.delete(`/employees/${e.id}`);
    toast.success("Deleted");
    load();
  };

  const resign = async (e) => {
    const today = new Date().toISOString().slice(0, 10);
    const dateStr = window.prompt(
      `Mark ${e.name} as resigned. Enter the last working day (YYYY-MM-DD):`,
      today
    );
    if (!dateStr) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      toast.error("Please use YYYY-MM-DD format");
      return;
    }
    const reason = window.prompt("Reason (optional):", "") || "";
    try {
      await api.post(`/employees/${e.id}/resign`, { resigned_date: dateStr, reason });
      toast.success(`${e.name} marked resigned · ${dateStr}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to resign");
    }
  };

  const reactivate = async (e) => {
    const ok = await confirm({
      title: "Reactivate employee?",
      message: `${e.name} will re-appear in the active roster and be eligible for payroll this month.`,
      tone: "info",
      confirmLabel: "Reactivate",
    });
    if (!ok) return;
    try {
      await api.post(`/employees/${e.id}/reactivate`);
      toast.success(`${e.name} reactivated`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reactivate");
    }
  };

  const totalSalary = emps.filter(e => e.active).reduce((s, e) => s + (e.monthly_salary || 0), 0);

  return (
    <div className="p-6 lg:p-10 bg-zinc-50 min-h-screen">
      <PageHeader
        label="HR & PAYROLL"
        title="Employees"
        subtitle={`${emps.filter(e => e.active).length} active employees · Monthly payroll ₹${totalSalary.toLocaleString("en-IN")}`}
        action={<Btn onClick={() => setOpen("new")} className="shadow-orange-500/20 shadow-lg"><Plus size={14} /> Add Employee</Btn>}
      />

      <Card className="!p-0 overflow-hidden shadow-sm border border-zinc-200">
        {emps.length === 0 ? (
          <div className="p-12"><EmptyState title="No employees yet" hint="Add your first employee to start tracking salary." /></div>
        ) : (
          <table className="w-full text-left border-collapse">
            <thead className="bg-zinc-100/50 text-zinc-500 uppercase text-[10px] tracking-widest font-bold">
              <tr>
                {isAdmin && <th className="p-4 w-8"><input type="checkbox" onChange={() => ms.toggleAll(emps.map(x => x.id))} className="accent-orange-600" /></th>}
                <th className="p-4">ID</th><th className="p-4">Name</th><th className="p-4">Role</th><th className="p-4">Joined</th><th className="p-4">Salary</th><th className="p-4">Docs</th><th className="p-4">Status</th><th className="p-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {emps.map(e => (
                <tr key={e.id} className="group hover:bg-zinc-50 transition-colors duration-200" data-testid={`emp-row-${e.emp_id}`}>
                  {isAdmin && <td className="p-4"><input type="checkbox" checked={ms.isSelected(e.id)} onChange={() => ms.toggle(e.id)} className="accent-orange-600" /></td>}
                  <td className="p-4 font-mono text-xs text-zinc-500">{e.emp_id}</td>
                  <td className="p-4 font-bold text-zinc-900">{e.name}</td>
                  <td className="p-4 text-sm text-zinc-600">{e.role}</td>
                  <td className="p-4 font-mono text-xs">{e.joining_date}</td>
                  <td className="p-4 font-mono font-bold">₹{e.monthly_salary?.toLocaleString("en-IN")}</td>
                  <td className="p-4">
                    <button onClick={() => setOpen(e)} className="flex items-center gap-1.5 text-xs font-mono bg-zinc-100 px-2 py-1 rounded-md hover:bg-orange-50 hover:text-orange-600 transition-all">
                      <Paperclip size={12} weight="bold" /> {(e.documents || []).length}
                    </button>
                  </td>
                  <td className="p-4">
                    {e.active ? (
                      <Badge tone="green">active</Badge>
                    ) : (
                      <div className="flex flex-col gap-0.5">
                        <Badge tone="zinc">inactive · resigned</Badge>
                        {e.resigned_date && (
                          <span className="text-[10px] font-mono text-zinc-500">
                            {e.resigned_date}{e.resigned_reason ? ` · ${e.resigned_reason}` : ""}
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="p-4 flex gap-3 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`emp-actions-${e.emp_id}`}>
                    <button onClick={() => setPay(e)} className="text-zinc-400 hover:text-orange-600" title="Payslip"><Calculator size={16} /></button>
                    <button onClick={() => setOpen(e)} className="text-zinc-400 hover:text-orange-600" title="Edit"><PencilSimple size={16} /></button>
                    {e.active ? (
                      <button onClick={() => resign(e)} className="text-zinc-400 hover:text-amber-600"
                              title="Mark as resigned" data-testid={`emp-resign-${e.emp_id}`}>
                        <UserMinus size={16} />
                      </button>
                    ) : (
                      <button onClick={() => reactivate(e)} className="text-zinc-400 hover:text-emerald-600"
                              title="Reactivate" data-testid={`emp-reactivate-${e.emp_id}`}>
                        <ArrowClockwise size={16} />
                      </button>
                    )}
                    <button onClick={() => del(e)} className="text-zinc-400 hover:text-red-600" title="Delete (permanent)"><Trash size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {open && <EmpModal emp={open === "new" ? null : open} onClose={() => setOpen(null)} onSaved={() => { setOpen(null); load(); }} />}
      {pay && <PayslipModal emp={pay} onClose={() => setPay(null)} />}
      <MultiSelectBar count={ms.selected.length} ids={ms.selected} resource="employees" label="employees" onCleared={() => { ms.clear(); load(); }} />
    </div>
  );
}

function EmpModal({ emp, onClose, onSaved }) {
  const isNew = !emp;
  const [f, setF] = useState(emp || {
    name: "", emp_id: "", role: "", joining_date: new Date().toISOString().slice(0, 10),
    monthly_salary: "", bank_account: "", contact: "", active: true,
    annual_leave_quota: 12, email: "",
  });
  const [docs, setDocs] = useState(emp?.documents || []);
  const upd = (k, v) => setF({ ...f, [k]: v });

  const refreshDocs = async () => {
    if (!emp) return;
    const r = await api.get("/employees");
    const fresh = r.data.find(x => x.id === emp.id);
    setDocs(fresh?.documents || []);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = { ...f, monthly_salary: parseFloat(f.monthly_salary) };
      if (isNew) await api.post("/employees", body);
      else await api.put(`/employees/${emp.id}`, body);
      toast.success(isNew ? "Added" : "Updated");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Modal open onClose={onClose} title={isNew ? "Add Employee" : `Edit · ${emp.name}`} size="lg">
      <form onSubmit={submit} className="grid grid-cols-2 gap-4" data-testid="emp-form">
        <Field label="Name"><Input value={f.name} onChange={e => upd("name", e.target.value)} required data-testid="emp-name" /></Field>
        <Field label="Employee ID">
          <Input value={f.emp_id}
                 onChange={e => upd("emp_id", e.target.value)}
                 placeholder={isNew ? "Auto-generated (leave blank)" : ""}
                 disabled={!isNew}
                 data-testid="emp-id" />
        </Field>
        <Field label="Role"><Input value={f.role} onChange={e => upd("role", e.target.value)} /></Field>
        <Field label="Joining Date"><DatePicker value={f.joining_date} onChange={e => upd("joining_date", e.target.value)} required /></Field>
        <Field label="Monthly Salary (₹)"><Input type="number" step="0.01" value={f.monthly_salary} onChange={e => upd("monthly_salary", e.target.value)} required data-testid="emp-salary" /></Field>
        <Field label="Contact"><Input value={f.contact} onChange={e => upd("contact", e.target.value)} /></Field>
        <Field label="Bank Account" full><Input value={f.bank_account} onChange={e => upd("bank_account", e.target.value)} /></Field>
        <Field label="Email (for notifications)"><Input type="email" value={f.email || ""} onChange={e => upd("email", e.target.value)} placeholder="Matches user account · optional" /></Field>
        <Field label="Annual Leave Quota (days)"><Input type="number" value={f.annual_leave_quota ?? 12} onChange={e => upd("annual_leave_quota", parseInt(e.target.value || 12))} data-testid="emp-leave-quota" /></Field>
        <Field label="Status">
          <Select value={f.active ? "1" : "0"} onChange={e => upd("active", e.target.value === "1")}>
            <option value="1">Active</option><option value="0">Inactive</option>
          </Select>
        </Field>

        {!isNew && (
          <div className="col-span-2 border-t border-zinc-200 pt-4 mt-2">
            <EmployeeDocuments emp={emp} docs={docs} onChanged={refreshDocs} />
          </div>
        )}

        <div className="col-span-2 flex justify-end gap-2">
          <Btn type="button" variant="secondary" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" data-testid="emp-submit">{isNew ? "Add" : "Save"}</Btn>
        </div>
      </form>
      {isNew && (
        <p className="text-[11px] font-mono text-zinc-500 mt-3">
          Save this employee first to upload Aadhar / ID proofs.
        </p>
      )}
    </Modal>
  );
}

// ------------------------------------------------ Employee documents (Aadhar / PAN etc.)
function EmployeeDocuments({ emp, docs, onChanged }) {
  const [label, setLabel] = useState("Aadhar");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = React.useRef(null);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose a file first"); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("File too large (max 8 MB)"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("label", label || "Document");
      fd.append("file", file);
      const res = await fetch(`${API}/employees/${emp.id}/documents`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Upload failed");
      }
      toast.success("Document uploaded");
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
      onChanged();
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally { setBusy(false); }
  };

  const download = async (d) => {
    try {
      const res = await fetch(`${API}/employees/${emp.id}/documents/${d.id}`, { credentials: "include" });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = d.original_name || `${d.label}${d.ext || ""}`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    } catch (e) { toast.error(e.message || "Download failed"); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.label}" (${d.original_name})?`)) return;
    try {
      await api.delete(`/employees/${emp.id}/documents/${d.id}`);
      toast.success("Removed");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="emp-docs-section">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-display text-sm font-black uppercase tracking-widest">ID Proof & Documents</div>
          <div className="text-[11px] font-mono text-zinc-500">Aadhar, PAN, offer letter, bank proofs, etc. Max 8 MB · PDF / JPG / PNG.</div>
        </div>
        <span className="text-xs font-mono text-zinc-500">{docs.length} file{docs.length === 1 ? "" : "s"}</span>
      </div>

      <div className="border border-zinc-200 divide-y divide-zinc-100 mb-3" data-testid="emp-docs-list">
        {docs.length === 0 ? (
          <div className="p-3 text-xs text-zinc-400 font-mono">No documents uploaded yet.</div>
        ) : docs.map(d => (
          <div key={d.id} className="flex items-center gap-3 p-2" data-testid={`doc-row-${d.id}`}>
            <FileText size={16} weight="bold" className="text-orange-600 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold truncate">{d.label}</div>
              <div className="text-[10px] font-mono text-zinc-500 truncate">
                {d.original_name} · {(d.size / 1024).toFixed(1)} KB · {new Date(d.uploaded_at).toLocaleDateString()}
              </div>
            </div>
            <button type="button" onClick={() => download(d)} className="text-orange-600 hover:text-orange-800" title="Download"
                    data-testid={`doc-download-${d.id}`}>
              <DownloadSimple size={14} weight="bold" />
            </button>
            <button type="button" onClick={() => remove(d)} className="text-red-600 hover:text-red-800" title="Delete"
                    data-testid={`doc-delete-${d.id}`}>
              <Trash size={14} weight="bold" />
            </button>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-[1fr_2fr_auto] gap-2 items-center">
        <Input value={label} onChange={e => setLabel(e.target.value)} placeholder="Label (e.g. Aadhar)"
               data-testid="doc-label-input" />
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,.webp,.heic"
          onChange={e => setFile(e.target.files?.[0] || null)}
          className="text-xs font-mono file:mr-2 file:py-1.5 file:px-3 file:border-0 file:bg-zinc-900 file:text-white file:font-bold file:uppercase file:tracking-widest file:text-[10px] file:cursor-pointer"
          data-testid="doc-file-input"
        />
        <button type="button" onClick={upload} disabled={busy || !file}
                className="px-3 py-1.5 bg-orange-600 text-white text-xs font-bold uppercase tracking-widest hover:bg-orange-700 disabled:opacity-50 flex items-center gap-1"
                data-testid="doc-upload-btn">
          <UploadSimple size={12} weight="bold" /> {busy ? "Uploading…" : "Upload"}
        </button>
      </div>
    </div>
  );
}

function PayslipModal({ emp, onClose }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/payroll/${emp.id}/${year}/${month}`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }, [emp.id, year, month]);
  useEffect(() => { load(); }, [load]);

  // --- Bonus editor (admin-only) ------------------------------------------------
  const [bLabel, setBLabel] = useState("");
  const [bAmount, setBAmount] = useState("");
  const [bBusy, setBBusy] = useState(false);
  const addBonus = async () => {
    const amt = parseFloat(bAmount);
    if (!bLabel.trim()) { toast.error("Enter a bonus label"); return; }
    if (!(amt > 0)) { toast.error("Enter a bonus amount greater than zero"); return; }
    setBBusy(true);
    try {
      await api.post(`/payroll/${emp.id}/${year}/${month}/bonuses`,
        { label: bLabel.trim(), amount: amt });
      toast.success("Bonus added");
      setBLabel(""); setBAmount("");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add bonus"); }
    finally { setBBusy(false); }
  };
  const delBonus = async (bid) => {
    try {
      await api.delete(`/payroll/bonuses/${bid}`);
      toast.success("Bonus removed");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!data) return (
    <Modal open onClose={onClose} title={`Payslip · ${emp.name}`}>
      <div className="text-zinc-500">Calculating…</div>
    </Modal>
  );

  // Shared PDF fetch — used by Download + WhatsApp flows.
  const fetchPayslipBlob = async () => {
    const res = await fetch(`${API}/payroll/${emp.id}/${year}/${month}/pdf`, { credentials: "include" });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `HTTP ${res.status}`);
    }
    const blob = await res.blob();
    const monthShort = new Date(year, month - 1).toLocaleString("default", { month: "short", year: "numeric" }).replace(/\s/g, "-");
    const filename = `Payslip-${(emp.name || "employee").replace(/\s+/g, "_")}-${monthShort}.pdf`;
    return { blob, filename };
  };

  const downloadPayslip = async () => {
    try {
      const { blob, filename } = await fetchPayslipBlob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
      toast.success("PDF downloaded");
    } catch (e) { toast.error(e.message || "Download failed"); }
  };

  // WhatsApp — attach PDF via native Share sheet on mobile, or download + open
  // WhatsApp Web with pre-filled text on desktop.
  const shareOnWhatsApp = async () => {
    let phone = (emp.contact || "").replace(/[^0-9]/g, "");
    if (!phone) {
      phone = window.prompt("Enter employee phone number (with country code, e.g. 919876543210):") || "";
      phone = phone.replace(/[^0-9]/g, "");
      if (!phone) return;
    }
    if (phone.length === 10) phone = "91" + phone;
    const monthName = new Date(year, month - 1).toLocaleString("default", { month: "long", year: "numeric" });
    const msg = `Hi ${emp.name},\n\n*Payslip — ${monthName}*\n\nGross: ${inr(data.monthly_salary)}\nWorking days: ${data.working_days}\nPaid days: ${data.paid_days}\nDeductions: ${inr(data.deductions)}\n*Net Payable: ${inr(data.net_payable)}*\n\n— Eath's HR`;

    try {
      const { blob, filename } = await fetchPayslipBlob();
      const file = new File([blob], filename, { type: "application/pdf" });

      // Mobile-first: native share sheet includes WhatsApp with the PDF attached.
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: `Payslip · ${monthName}`, text: msg });
        toast.success("Shared to WhatsApp");
        return;
      }

      // Desktop fallback: auto-download the PDF, then open WhatsApp Web
      // with a pre-filled message. User attaches the just-downloaded file.
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);

      const url = `https://wa.me/${phone}?text=${encodeURIComponent(msg + "\n\n(PDF payslip has been downloaded — please attach it to this chat)")}`;
      const win = window.open(url, "_blank", "noopener,noreferrer");
      if (!win) window.location.href = url;
      toast.info("Payslip PDF downloaded. Attach it in the WhatsApp chat that just opened.");
    } catch (e) {
      // If PDF fetch fails, degrade to the old text-only WA link.
      toast.error(`Couldn't attach PDF (${e.message}). Falling back to text-only WhatsApp.`);
      const url = `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
      const win = window.open(url, "_blank", "noopener,noreferrer");
      if (!win) window.location.href = url;
    }
  };

  return (
    <Modal open onClose={onClose} title={`Payslip · ${emp.name}`} size="lg">
      <div className="print:hidden mb-4 flex items-center gap-3 flex-wrap">
        <Select value={month} onChange={e => setMonth(parseInt(e.target.value))} className="!w-32">
          {Array.from({ length: 12 }, (_, i) => i + 1).map(m =>
            <option key={m} value={m}>{new Date(0, m - 1).toLocaleString("default", { month: "long" })}</option>
          )}
        </Select>
        <Select value={year} onChange={e => setYear(parseInt(e.target.value))} className="!w-32">
          {[2024, 2025, 2026, 2027].map(y => <option key={y}>{y}</option>)}
        </Select>
        <Btn variant="secondary" onClick={() => printDom('[data-testid="payslip-content"]', `Payslip ${emp.name} ${new Date(year, month - 1).toLocaleString("default", { month: "short", year: "numeric" })}`)} data-testid="print-payslip"><Printer size={14} /> Print</Btn>
        <Btn variant="secondary" onClick={downloadPayslip} data-testid="download-payslip"><DownloadSimple size={14} weight="bold" /> Download PDF</Btn>
        <Btn variant="secondary" onClick={shareOnWhatsApp} data-testid="wa-payslip"><WhatsappLogo size={14} weight="bold" /> WhatsApp</Btn>
      </div>

      <div data-testid="payslip-content">
        <Letterhead
          docTitle="PAYSLIP"
          docMeta={[
            ["For", new Date(year, month - 1).toLocaleString("default", { month: "long", year: "numeric" })],
            ["Employee", emp.name],
            ["Emp ID", emp.emp_id],
            ["Role", emp.role],
          ]}
        >

        <div className="grid grid-cols-4 gap-4 mb-4">
          <div><div className="kr-label">Days in month</div><div className="font-mono">{data.days_in_month}</div></div>
          <div><div className="kr-label">Sundays (off)</div><div className="font-mono">{data.sundays}</div></div>
          <div><div className="kr-label">Working days</div><div className="font-mono font-bold">{data.working_days}</div></div>
          <div><div className="kr-label">Per-day salary</div><div className="font-mono font-bold">{inr(data.per_day_salary)}</div></div>
        </div>

        <div className="border border-zinc-200 mb-4">
          <table className="kr-table">
            <thead><tr><th>Attendance breakdown</th><th>Days</th></tr></thead>
            <tbody>
              <tr><td>Present</td><td className="font-mono">{data.counts.present || 0}</td></tr>
              {(data.counts.special_holiday || 0) > 0 && (
                <tr><td>Special holiday (paid)</td><td className="font-mono">{data.counts.special_holiday}</td></tr>
              )}
              <tr>
                <td>Leave (policy: {data.allotted_leaves ?? data.policy?.leave ?? data.policy?.casual ?? 0}/month)</td>
                <td className="font-mono">{data.counts.leave || 0}</td>
              </tr>
              {(data.counts.half_day || 0) > 0 && (
                <tr><td>Half day (paid ×0.5)</td><td className="font-mono">{data.counts.half_day}</td></tr>
              )}
              {(data.counts.absent || 0) > 0 && (
                <tr className="text-red-700"><td>Absent (legacy)</td><td className="font-mono">{data.counts.absent}</td></tr>
              )}
              <tr className="font-bold"><td>Total paid days</td><td className="font-mono">{data.paid_days}</td></tr>
              {(data.excess_leaves || 0) > 0 && (
                <tr className="text-red-700"><td>Excess leaves (deducted)</td><td className="font-mono">{data.excess_leaves}</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Bonus editor (admin-only) — print-hidden */}
        {isAdmin && (
          <div className="mb-4 border border-zinc-200 bg-white print:hidden" data-testid="bonus-editor">
            <div className="px-3 py-2 border-b border-zinc-200 bg-zinc-50">
              <div className="kr-label">Bonuses (added AFTER deductions)</div>
              <div className="text-[10px] font-mono text-zinc-500 mt-0.5">Diwali, Performance, Referral, etc.</div>
            </div>
            <div className="p-3 space-y-2">
              {(data.bonuses || []).length === 0 && (
                <div className="text-xs text-zinc-500 font-mono">No bonuses added for this month yet.</div>
              )}
              {(data.bonuses || []).map(b => (
                <div key={b.id} className="flex items-center gap-3 border border-zinc-200 p-2" data-testid={`bonus-row-${b.id}`}>
                  <div className="flex-1">
                    <div className="font-bold text-sm">{b.label}</div>
                    <div className="text-[10px] font-mono text-zinc-500">Added</div>
                  </div>
                  <div className="font-mono font-bold text-green-700">+ {inr(b.amount)}</div>
                  <button type="button" onClick={() => delBonus(b.id)}
                          className="text-red-600 hover:text-red-800 text-xs font-bold uppercase tracking-widest"
                          data-testid={`bonus-del-${b.id}`}>Remove</button>
                </div>
              ))}
              <div className="grid grid-cols-[1fr_140px_auto] gap-2 items-end pt-2 border-t border-zinc-100">
                <Field label="Bonus label">
                  <Input value={bLabel} onChange={e => setBLabel(e.target.value)}
                         placeholder="e.g. Diwali bonus" data-testid="bonus-label-input" />
                </Field>
                <Field label="Amount (₹)">
                  <Input type="number" step="0.01" value={bAmount}
                         onChange={e => setBAmount(e.target.value)}
                         placeholder="0.00" data-testid="bonus-amount-input" />
                </Field>
                <Btn type="button" onClick={addBonus} disabled={bBusy} data-testid="bonus-add-btn">
                  {bBusy ? "Adding…" : "+ Add"}
                </Btn>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <div className="w-80 border border-zinc-300 p-3 bg-zinc-50 font-mono text-sm" data-testid="payslip-totals">
            <div className="flex justify-between"><span>Gross Salary</span><span>{inr(data.monthly_salary)}</span></div>
            {data.deduction_breakdown && (
              <>
                <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500">Deductions</div>
                {(data.deduction_breakdown.absent || 0) > 0 && (
                  <div className="flex justify-between text-red-700 text-xs" data-testid="ded-absent">
                    <span>· Absent ({data.counts.absent || 0} days)</span>
                    <span>− {inr(data.deduction_breakdown.absent)}</span>
                  </div>
                )}
                {(data.deduction_breakdown.excess_leave || 0) > 0 && (
                  <div className="flex justify-between text-red-700 text-xs" data-testid="ded-excess-leave">
                    <span>· Excess leaves ({data.excess_leaves || 0} over quota)</span>
                    <span>− {inr(data.deduction_breakdown.excess_leave)}</span>
                  </div>
                )}
              </>
            )}
            <div className="flex justify-between text-red-700 border-t border-zinc-300 mt-1 pt-1 font-bold">
              <span>Total Deductions</span><span>− {inr(data.deductions)}</span>
            </div>
            <div className="flex justify-between border-t border-zinc-300 mt-1 pt-1 text-sm">
              <span>Base Payable</span><span>{inr(data.base_payable ?? (data.monthly_salary - data.deductions))}</span>
            </div>
            {(data.bonuses || []).length > 0 && (
              <>
                <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500">Bonuses</div>
                {data.bonuses.map(b => (
                  <div key={b.id} className="flex justify-between text-green-700 text-xs" data-testid={`payslip-bonus-${b.id}`}>
                    <span>· {b.label}</span>
                    <span>+ {inr(b.amount)}</span>
                  </div>
                ))}
                <div className="flex justify-between text-green-700 font-bold border-t border-zinc-300 mt-1 pt-1" data-testid="payslip-bonus-total">
                  <span>Total Bonus</span><span>+ {inr(data.bonus_total || 0)}</span>
                </div>
              </>
            )}
            <div className="flex justify-between font-bold border-t-2 border-zinc-900 mt-1 pt-2 text-base" data-testid="payslip-net">
              <span>NET PAYABLE</span><span>{inr(data.net_payable)}</span>
            </div>
          </div>
        </div>

        </Letterhead>
      </div>

      <div className="mt-6 text-xs text-zinc-500 print:hidden">
        Payslip computed: Working days = {data.days_in_month} − {data.sundays} (Sundays) = {data.working_days}.
        Per-day = {inrRound(data.monthly_salary)} / {data.working_days} = {inr(data.per_day_salary)}.
      </div>
    </Modal>
  );
}
